import React from "react";
import useApi from "@/hooks/useApi";
import { PrimaryBlueButton, LoadingSpinner } from "@velaro/velaro-component-lib";
import FacebookIntegrationEditModal from "./FacebookIntegrationEditModal";

interface FacebookPage {
  id: number;
  pageName: string;
  groupName: string;
}

// FB SDK types
declare global {
  interface Window {
    FB: any;
    fbAsyncInit: () => void;
  }
}

export default function FacebookIntegration() {
  const [loading, setLoading] = React.useState(true);
  const [editId, setEditId] = React.useState<number | undefined>();
  const [integrations, setIntegrations] = React.useState<FacebookPage[]>([]);
  const [provisioning, setProvisioning] = React.useState(false);
  const [sdkLoaded, setSdkLoaded] = React.useState(false);
  const [message, setMessage] = React.useState<{ text: string; type: "success" | "error" } | null>(null);
  const api = useApi();

  const fetchIntegrations = React.useCallback(async () => {
    try {
      const response = await api.messaging.get("Facebook/Integrations");
      const data = await response.json();
      setIntegrations(Array.isArray(data) ? data : []);
    } catch {
      setIntegrations([]);
    } finally {
      setLoading(false);
    }
  }, [api.messaging]);

  React.useEffect(() => {
    fetchIntegrations();
  }, [fetchIntegrations]);

  // Load Meta SDK
  React.useEffect(() => {
    if ((window as any).FB) {
      setSdkLoaded(true);
      return;
    }
    const script = document.createElement("script");
    script.id = "facebook-jssdk";
    script.src = "https://connect.facebook.net/en_US/sdk.js";
    script.addEventListener("load", () => setSdkLoaded(true));
    document.body.appendChild(script);
  }, []);

  React.useEffect(() => {
    if (!sdkLoaded) return;
    const appId = import.meta.env.VITE_FACEBOOK_APP_ID;
    if (!appId) return;
    (window as any).FB.init({ appId, cookie: true, xfbml: true, version: "v20.0" });
  }, [sdkLoaded]);

  const handleConnect = React.useCallback(() => {
    if (!sdkLoaded || !(window as any).FB) {
      setMessage({ text: "Meta SDK not loaded. Please refresh and try again.", type: "error" });
      return;
    }
    setProvisioning(true);
    setMessage(null);

    (window as any).FB.login(
      async (response: any) => {
        if (response.status !== "connected") {
          setMessage({ text: "Authorization cancelled or failed.", type: "error" });
          setProvisioning(false);
          return;
        }
        try {
          const resp = await api.messaging.post("Facebook/Provision", {
            userId: response.authResponse.userID,
            userAccessToken: response.authResponse.accessToken
          });
          if (!resp.ok) {
            setMessage({ text: "Failed to connect Facebook Pages. Please try again.", type: "error" });
          } else {
            setMessage({ text: "Facebook Pages connected successfully.", type: "success" });
            fetchIntegrations();
          }
        } catch {
          setMessage({ text: "Failed to connect Facebook. Please try again.", type: "error" });
        } finally {
          setProvisioning(false);
        }
      },
      { scope: "pages_show_list,pages_messaging,public_profile,email" }
    );
  }, [sdkLoaded, api.messaging, fetchIntegrations]);

  async function removeIntegration(integrationId: number) {
    if (!window.confirm("Remove this Facebook Page from Velaro?")) return;
    try {
      await api.messaging.delete(`Facebook/Integrations/${integrationId}`);
      fetchIntegrations();
    } catch {
      setMessage({ text: "Failed to remove page. Please try again.", type: "error" });
    }
  }

  if (loading) return <div className="flex justify-center py-20"><LoadingSpinner /></div>;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ background: "#1877F2" }}
        >
          <svg viewBox="0 0 24 24" className="w-5 h-5 fill-white" aria-hidden="true">
            <path d="M24 12.073C24 5.404 18.627 0 12 0S0 5.404 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.41c0-3.025 1.792-4.697 4.533-4.697 1.312 0 2.686.236 2.686.236v2.97h-1.513c-1.491 0-1.956.93-1.956 1.884v2.25h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z" />
          </svg>
        </div>
        <div>
          <h2 className="text-lg font-semibold text-slate-800">Facebook Messenger</h2>
          <p className="text-sm text-slate-500">
            Connect Facebook Pages to receive and respond to Messenger conversations in the Velaro
            inbox. Requires admin access to the Facebook Page.
          </p>
        </div>
      </div>

      {/* Status message */}
      {message && (
        <div
          className={`px-4 py-3 rounded-md text-sm ${
            message.type === "success"
              ? "bg-green-50 border border-green-200 text-green-800"
              : "bg-red-50 border border-red-200 text-red-800"
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Connected pages */}
      {integrations.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full bg-white border border-slate-200 rounded-lg text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Page</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Team</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Status</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600" style={{ width: 1 }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {integrations.map((page) => (
                <tr key={page.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ background: "#1877F2" }}
                      >
                        <svg viewBox="0 0 24 24" className="w-4 h-4 fill-white" aria-hidden="true">
                          <path d="M24 12.073C24 5.404 18.627 0 12 0S0 5.404 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.41c0-3.025 1.792-4.697 4.533-4.697 1.312 0 2.686.236 2.686.236v2.97h-1.513c-1.491 0-1.956.93-1.956 1.884v2.25h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z" />
                        </svg>
                      </div>
                      <span className="font-medium text-slate-800">{page.pageName}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{page.groupName || "All teams"}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                      Active
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <button
                      className="text-blue-600 hover:text-blue-800 underline text-xs"
                      onClick={() => setEditId(page.id)}
                      aria-label={`Edit ${page.pageName}`}
                    >
                      Edit
                    </button>
                    <span className="mx-2 text-slate-300">|</span>
                    <button
                      className="text-red-500 hover:text-red-700 underline text-xs"
                      onClick={() => removeIntegration(page.id)}
                      aria-label={`Remove ${page.pageName}`}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {integrations.length === 0 && !loading && (
        <div className="text-center py-10 bg-slate-50 rounded-lg border border-dashed border-slate-200">
          <div
            className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3"
            style={{ background: "#1877F2" }}
          >
            <svg viewBox="0 0 24 24" className="w-6 h-6 fill-white" aria-hidden="true">
              <path d="M24 12.073C24 5.404 18.627 0 12 0S0 5.404 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.41c0-3.025 1.792-4.697 4.533-4.697 1.312 0 2.686.236 2.686.236v2.97h-1.513c-1.491 0-1.956.93-1.956 1.884v2.25h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z" />
            </svg>
          </div>
          <p className="text-slate-600 font-medium">No Facebook Pages connected</p>
          <p className="text-slate-400 text-sm mt-1">
            Connect a Facebook Page to receive Messenger conversations in your inbox.
          </p>
        </div>
      )}

      {/* Connect button */}
      <div>
        <PrimaryBlueButton
          label={provisioning ? "Connecting..." : "Connect Facebook Pages"}
          onClick={handleConnect}
          disabled={provisioning}
        />
        <p className="text-xs text-slate-400 mt-2">
          You'll be prompted to log in with Facebook and select which Pages to connect. You must be
          an admin of the Page to authorize Velaro.
        </p>
      </div>

      {editId !== undefined && (
        <FacebookIntegrationEditModal
          integrationId={editId}
          onClose={() => setEditId(undefined)}
          onUpdate={() => {
            setEditId(undefined);
            fetchIntegrations();
          }}
        />
      )}
    </div>
  );
}
